import { doc, getDoc, updateDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { db } from '../../config';
import { shareScope } from '../../../../constants/enum';
import { type ItemListType } from '../../../../types/common';
import { PatternFileType, type ShareScope } from '../../../../types/pattern';
import { uploadPatternsToStorage, deletePatternFilesFromStorage } from '../../storage';
import { getTotalItemCount } from '../../../../utils/pattern/validation';
import { convertItemListToSnakeCase } from '../../../../utils/pattern/itemListConverter';
import { syncMasterMappingLabels } from '../master/mapping/syncLabels';
import { syncDictionaryMappingLabels } from '../dictionary/mapping/syncLabels';
import { recordClientAuditEvent } from '../../function/security/clientAuditEventFunction';
import { auditAction, auditResourceType } from '../../../../constants/enum';
import { syncResultsShareScopeByPattern } from '../result/syncShareScope';

/** 順不同で2つのID配列が同一集合かを判定する */
const isSameIdSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

/**
 * 読取パターンのitem_listのみを更新
 */
export const updatePatternItemList = async (
  patternId: string,
  itemList: ItemListType[],
  userId: string,
): Promise<void> => {
  const patternRef = doc(db, 'patterns', patternId);
  await updateDoc(patternRef, {
    item_list: convertItemListToSnakeCase(itemList),
    item_count: getTotalItemCount(itemList),
    updated_at: serverTimestamp(),
    updated_by: userId,
  });
  // 項目名が変わった場合に備え、このパターンを参照するマスタ・辞書マッピングのラベルを同期
  await syncMasterMappingLabels(patternId, itemList);
  await syncDictionaryMappingLabels(patternId, itemList);

  // 監査ログ（#2985）: パターン編集（項目リスト）。fire-and-forget で主処理を遅らせない
  void recordClientAuditEvent({
    action: auditAction.update,
    resource_type: auditResourceType.readPattern,
    resource_id: patternId,
  });
};

/**
 * 読取パターンを更新
 */
export const updatePattern = async (
  patternId: string,
  patternName: string,
  documentName: string | undefined,
  description: string | undefined,
  remarks: string | undefined,
  fileType: number,
  patternData: ItemListType[],
  isPublic: boolean,
  userId: string,
  newFiles?: File[], // 新規アップロードファイル → original_sample_files
  existingOriginalFiles?: PatternFileType[], // 既存のoriginal_sample_files（維持するファイル）
  geminiModel?: number,
  prompt?: string,
  useCustomPrompt?: boolean,
  isClassificationTarget?: boolean,
  scope?: ShareScope, // 共有範囲（#2804 Phase2）
  sharedGroupIds?: string[], // グループ限定時の対象グループID
  sharedUserIds?: string[], // 限定共有時の対象担当者UID（#2963）
  ocrEngine?: number, // OCRエンジン設定（#3264 ocrEngineType・運営スタッフ限定）
): Promise<void> => {
  const patternRef = doc(db, 'patterns', patternId);

  const currentPattern = await getDoc(patternRef);
  const currentData = currentPattern.data();
  const companyId = (currentData?.company_id as string) || '';

  // 共有範囲が実際に変わった場合、pattern_id 一致の既存OCR結果へ先に追従伝播する（#2973）。
  // 他の変更（Storage・パターン更新）より前に実行することで、伝播失敗時は他が未変更のまま残り、
  // 再保存で shareChanged が再度真になって確実に再試行できる（partial 保存を防ぐ）。
  // 未変更（名称変更のみ等）では無駄な一括書き込みを走らせないよう差分を判定する。
  if (scope !== undefined) {
    const prevScope = (currentData?.share_scope as ShareScope | undefined) ?? shareScope.company;
    const prevGroups = (currentData?.shared_group_ids as string[] | undefined) ?? [];
    const prevUsers = (currentData?.shared_user_ids as string[] | undefined) ?? [];
    const nextGroups = scope === shareScope.group ? (sharedGroupIds ?? []) : [];
    const nextUsers = scope === shareScope.group ? (sharedUserIds ?? []) : [];
    const shareChanged =
      prevScope !== scope ||
      !isSameIdSet(prevGroups, nextGroups) ||
      !isSameIdSet(prevUsers, nextUsers);
    if (shareChanged) {
      try {
        await syncResultsShareScopeByPattern(patternId, {
          shareScope: scope,
          sharedGroupIds: nextGroups,
          sharedUserIds: nextUsers,
        });
      } catch (e) {
        Sentry.captureException(e);
        throw new Error(
          '既存の読取結果への共有範囲の反映に失敗しました。パターンは保存されていません。お手数ですが、もう一度保存してください。',
        );
      }
    }
  }

  // 既存のoriginal_sample_filesのうち削除されたものをStorageから除去
  const currentOriginalFiles = (currentData?.original_sample_files as PatternFileType[]) || [];
  const originalFilesToDelete = currentOriginalFiles.filter(
    currentFile => !existingOriginalFiles?.some(ef => ef.storage_path === currentFile.storage_path),
  );
  if (originalFilesToDelete.length > 0) {
    await deletePatternFilesFromStorage(originalFilesToDelete);
  }

  // 新規ファイルをアップロード → original_sample_files
  let newUploadedFiles: PatternFileType[] = [];
  if (newFiles && newFiles.length > 0) {
    newUploadedFiles = await uploadPatternsToStorage(
      newFiles,
      patternId,
      'originals',
      companyId || undefined,
    );
  }

  const finalOriginalFiles = [...(existingOriginalFiles || []), ...newUploadedFiles];

  await updateDoc(patternRef, {
    pattern_name: patternName,
    document_name: documentName || '',
    description: description || '',
    remarks: remarks || '',
    file_type: fileType,
    item_count: getTotalItemCount(patternData),
    item_list: convertItemListToSnakeCase(patternData),
    is_public: isPublic,
    original_sample_files: finalOriginalFiles,
    ...(geminiModel !== undefined && { gemini_model: geminiModel }),
    ...(ocrEngine !== undefined && { ocr_engine: ocrEngine }), // #3264
    ...(prompt !== undefined && { prompt }),
    ...(useCustomPrompt !== undefined && { use_custom_prompt: useCustomPrompt }),
    is_classification_target: isClassificationTarget !== false,
    // 共有範囲（#2804 Phase2 / 担当者指定 #2963）。owner_user_id は作成者を維持するため更新しない
    ...(scope !== undefined && {
      share_scope: scope,
      shared_group_ids: scope === shareScope.group ? (sharedGroupIds ?? []) : [],
      shared_user_ids: scope === shareScope.group ? (sharedUserIds ?? []) : [],
    }),
    updated_at: serverTimestamp(),
    updated_by: userId,
  });
  // 項目名・パターン名が変わった場合に備え、このパターンを参照するマスタ・辞書マッピングの
  // ラベルとパターン名を同期する。
  await syncMasterMappingLabels(patternId, patternData, patternName);
  await syncDictionaryMappingLabels(patternId, patternData, patternName);

  // 監査ログ（#2985）: パターン編集。fire-and-forget で主処理を遅らせない
  void recordClientAuditEvent({
    action: auditAction.update,
    resource_type: auditResourceType.readPattern,
    resource_id: patternId,
  });
};

/**
 * 複数パターンの display_order を一括更新
 * @param orderedPatternIds - 表示順に並んだパターンIDの配列
 */
export const updatePatternsDisplayOrder = async (orderedPatternIds: string[]): Promise<void> => {
  const batch = writeBatch(db);

  orderedPatternIds.forEach((patternId, index) => {
    const patternRef = doc(db, 'patterns', patternId);
    batch.update(patternRef, { display_order: index });
  });

  await batch.commit();
};
